/**
 * G-27 kit: what dictated words do to a field, as a pure rule.
 *
 * Separate from `useDictation` so it is tested without React or a microphone —
 * the discipline `printer.ts` uses to keep `chunkForMtu` and `readFlowControl`
 * provable without a Bluetooth stack. It is also the rule most likely to be got
 * wrong twice if two screens ever implement it themselves.
 */

/**
 * Join dictated words onto what is already in the field.
 *
 * APPEND, NEVER REPLACE (G-27 interaction rule 1). Replacing would silently
 * destroy typed text, which is the one failure that would get this feature
 * switched off and never switched back on.
 *
 * `limit` is the FIELD'S cap, and it is enforced here because a textarea's
 * `maxLength` attribute does not apply to a programmatic write — the note screen
 * would happily hold 600 characters and then print a silently truncated sticker.
 */
export function appendDictated(
  existing: string,
  addition: string,
  limit?: number,
): string {
  const words = addition.trim();
  if (words.length === 0) return existing;

  // A single space, and only where there is something to separate from: leading
  // whitespace in an empty field reads as a bug to the person who then types.
  // An existing trailing space is left alone rather than doubled.
  const joined =
    existing.length === 0 || /\s$/.test(existing)
      ? existing + words
      : `${existing} ${words}`;

  if (limit === undefined || joined.length <= limit) return joined;

  // Cut at the limit rather than dropping the whole utterance. A florist who
  // said too much should see most of it and delete a word, not watch nothing
  // happen and wonder whether the microphone heard them at all.
  return joined.slice(0, limit);
}
